// src/components/MechanicsBarcode.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBarcode, faPrint, faTags, faBoxes, faSlidersH, faCog,
    faTimes, faCheck, faExclamationTriangle, faSearch, faSpinner,
    faPlus, faMinus, faEye, faSync, faStore, faRefresh,
    faTrashAlt, faChevronDown, faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import JsBarcode from 'jsbarcode';
import { useReactToPrint } from 'react-to-print';
import '../module_mechanics/Styles/BarUI.css';

// ─────────────────────────────────────────────────────────────
// 🌍 TRANSLATION SYSTEM
// ─────────────────────────────────────────────────────────────
const createBarcodeTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        const dict = {
            'ur': {
                'Barcode Generator': 'بارکوڈ جنریٹر',
                'Select Products': 'پروڈکٹس منتخب کریں',
                'Selected': 'منتخب کردہ',
                'No products selected': 'کوئی پروڈکٹ منتخب نہیں',
                'Barcode Style': 'بارکوڈ اسٹائل',
                'Type': 'قسم',
                'Width': 'چوڑائی',
                'Height': 'اونچائی',
                'Font Size': 'فونٹ سائز',
                'Label Content': 'لیبل مواد',
                'Product Name': 'پروڈکٹ کا نام',
                'Price': 'قیمت',
                'Batch No': 'بیچ نمبر',
                'Business Name': 'کاروبار کا نام',
                'Paper & Printer': 'کاغذ اور پرنٹر',
                'Label Sheet': 'لیبل شیٹ',
                'Spacing': 'فاصلہ',
                'Orientation': 'سمت',
                'Portrait': 'عمودی',
                'Landscape': 'افقی',
                'Quantity': 'مقدار',
                'Custom': 'اپنی مرضی',
                'Stock Qty': 'اسٹاک مقدار',
                'Print Copies': 'پرنٹ کاپیاں',
                'Preview': 'پیش نظارہ',
                'Print': 'پرنٹ کریں',
                'Printing...': 'پرنٹ ہو رہا ہے...',
                'Cancel': 'منسوخ کریں',
                'Print Now': 'ابھی پرنٹ کریں',
                'Select All': 'سب منتخب کریں',
                'Clear': 'صاف کریں',
                'Search products...': 'پروڈکٹس تلاش کریں...',
                'No products found': 'کوئی پروڈکٹ نہیں ملی',
                'Loading products...': 'پروڈکٹس لوڈ ہو رہے ہیں...',
                'Retry': 'دوبارہ کوشش کریں',
                'Batch': 'بیچ',
                'Stock': 'اسٹاک',
                'Rs.': 'روپے',
                'Success': 'کامیابی',
                'Error': 'خرابی',
                'Warning': 'انتباہ',
                'setPerProduct': 'فی پروڈکٹ مقرر کریں',
                'onePerUnit': '1 فی یونٹ'
            },
            'ar': {
                'Barcode Generator': 'مولد الباركود',
                'Select Products': 'اختر المنتجات',
                'Selected': 'المحدد',
                'No products selected': 'لم يتم اختيار منتجات',
                'Barcode Style': 'نمط الباركود',
                'Type': 'النوع',
                'Width': 'العرض',
                'Height': 'الارتفاع',
                'Font Size': 'حجم الخط',
                'Label Content': 'محتوى الملصق',
                'Product Name': 'اسم المنتج',
                'Price': 'السعر',
                'Batch No': 'رقم الدفعة',
                'Business Name': 'اسم النشاط التجاري',
                'Paper & Printer': 'الورق والطابعة',
                'Label Sheet': 'ورقة الملصقات',
                'Spacing': 'التباعد',
                'Orientation': 'الاتجاه',
                'Portrait': 'عمودي',
                'Landscape': 'أفقي',
                'Quantity': 'الكمية',
                'Custom': 'مخصص',
                'Stock Qty': 'كمية المخزون',
                'Print Copies': 'نسخ الطباعة',
                'Preview': 'معاينة',
                'Print': 'طباعة',
                'Printing...': 'جاري الطباعة...',
                'Cancel': 'إلغاء',
                'Print Now': 'طباعة الآن',
                'Select All': 'اختر الكل',
                'Clear': 'مسح',
                'Search products...': 'بحث عن منتجات...',
                'No products found': 'لم يتم العثور على منتجات',
                'Loading products...': 'جاري تحميل المنتجات...',
                'Retry': 'إعادة المحاولة',
                'Batch': 'دفعة',
                'Stock': 'المخزون',
                'Rs.': 'روبية',
                'Success': 'نجاح',
                'Error': 'خطأ',
                'Warning': 'تحذير',
                'setPerProduct': 'تعيين لكل منتج',
                'onePerUnit': '1 لكل وحدة'
            },
            'es': {
                'Barcode Generator': 'Generador de Códigos de Barras',
                'Select Products': 'Seleccionar Productos',
                'Selected': 'Seleccionados',
                'No products selected': 'No hay productos seleccionados',
                'Barcode Style': 'Estilo de Código de Barras',
                'Type': 'Tipo',
                'Width': 'Ancho',
                'Height': 'Alto',
                'Font Size': 'Tamaño de Fuente',
                'Label Content': 'Contenido de la Etiqueta',
                'Product Name': 'Nombre del Producto',
                'Price': 'Precio',
                'Batch No': 'Número de Lote',
                'Business Name': 'Nombre del Negocio',
                'Paper & Printer': 'Papel e Impresora',
                'Label Sheet': 'Hoja de Etiquetas',
                'Spacing': 'Espaciado',
                'Orientation': 'Orientación',
                'Portrait': 'Vertical',
                'Landscape': 'Horizontal',
                'Quantity': 'Cantidad',
                'Custom': 'Personalizado',
                'Stock Qty': 'Cantidad en Stock',
                'Print Copies': 'Copias de Impresión',
                'Preview': 'Vista Previa',
                'Print': 'Imprimir',
                'Printing...': 'Imprimiendo...',
                'Cancel': 'Cancelar',
                'Print Now': 'Imprimir Ahora',
                'Select All': 'Seleccionar Todo',
                'Clear': 'Limpiar',
                'Search products...': 'Buscar productos...',
                'No products found': 'No se encontraron productos',
                'Loading products...': 'Cargando productos...',
                'Retry': 'Reintentar',
                'Batch': 'Lote',
                'Stock': 'Stock',
                'Rs.': 'Rs.',
                'Success': 'Éxito',
                'Error': 'Error',
                'Warning': 'Advertencia',
                'setPerProduct': 'Establecer por producto',
                'onePerUnit': '1 por unidad'
            }
        };

        if (dict[targetLang] && dict[targetLang][text]) {
            return dict[targetLang][text];
        }

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("Translation failed");
        }
        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;
        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }
        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();
        return translated;
    };

    const defaultTexts = {
        barcodeGenerator: 'Barcode Generator',
        selectProducts: 'Select Products',
        selected: 'Selected',
        noProductsSelected: 'No products selected',
        barcodeStyle: 'Barcode Style',
        type: 'Type',
        width: 'Width',
        height: 'Height',
        fontSize: 'Font Size',
        labelContent: 'Label Content',
        productName: 'Product Name',
        price: 'Price',
        batchNo: 'Batch No',
        businessName: 'Business Name',
        paperPrinter: 'Paper & Printer',
        labelSheet: 'Label Sheet',
        spacing: 'Spacing',
        orientation: 'Orientation',
        portrait: 'Portrait',
        landscape: 'Landscape',
        quantity: 'Quantity',
        custom: 'Custom',
        stockQty: 'Stock Qty',
        printCopies: 'Print Copies',
        preview: 'Preview',
        print: 'Print',
        printing: 'Printing...',
        cancel: 'Cancel',
        printNow: 'Print Now',
        selectAll: 'Select All',
        clear: 'Clear',
        searchProducts: 'Search products...',
        noProductsFound: 'No products found',
        loadingProducts: 'Loading products...',
        retry: 'Retry',
        batch: 'Batch',
        stock: 'Stock',
        rs: 'Rs.',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        setPerProduct: 'Set per product',
        onePerUnit: '1 per unit'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') return defaultTexts;
        const translations = {};
        const promises = [];
        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });
        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }
        return translations;
    };

    loadFromCache();
    return { getTranslations, translateText };
};

const barcodeTranslator = createBarcodeTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations.success || 'Success';
            case 'error': return translations.error || 'Error';
            case 'warning': return translations.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    return (
        <div className={`ozui_toast-notification ${toast.type}`}>
            <div className="ozui_toast-content">
                <div className="ozui_toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="ozui_toast-text">
                    <div className="ozui_toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozui_toast-message">{toast.message}</div>
                </div>
                <button className="ozui_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────
// 🖨️ PAPER PRESETS
// ─────────────────────────────────────────────────────────────
const PAPER_PRESETS = {
    'zebra_4x6': {
        name: 'Zebra 4" x 6" (Shipping)',
        paper: '4x6in',
        labelsPerRow: 1,
        labelWidth: 101.6,
        labelHeight: 152.4,
        marginTop: 0,
        marginLeft: 0,
        description: 'Standard thermal shipping label'
    },
    'zebra_2x1': {
        name: 'Zebra 2" x 1" (Small)',
        paper: '2x1in',
        labelsPerRow: 3,
        labelWidth: 50.8,
        labelHeight: 25.4,
        marginTop: 5,
        marginLeft: 5,
        description: 'Small price/barcode labels'
    },
    'zebra_2x2': {
        name: 'Zebra 2" x 2" (Square)',
        paper: '2x2in',
        labelsPerRow: 2,
        labelWidth: 50.8,
        labelHeight: 50.8,
        marginTop: 5,
        marginLeft: 5,
        description: 'Square product labels'
    },
    'zebra_3x2': {
        name: 'Zebra 3" x 2"',
        paper: '3x2in',
        labelsPerRow: 2,
        labelWidth: 76.2,
        labelHeight: 50.8,
        marginTop: 5,
        marginLeft: 5,
        description: 'Medium rectangular labels'
    },
    'a4_3x8': {
        name: 'A4 - 3 col x 8 row',
        paper: 'A4',
        labelsPerRow: 3,
        labelWidth: 63.5,
        labelHeight: 33.9,
        marginTop: 15,
        marginLeft: 14,
        description: 'Standard address labels'
    },
    'letter_3x10': {
        name: 'Letter - 3 col x 10 row',
        paper: 'Letter',
        labelsPerRow: 3,
        labelWidth: 63.5,
        labelHeight: 25.4,
        marginTop: 12.7,
        marginLeft: 14,
        description: 'US Letter size labels'
    }
};

const API_URL = 'https://ourzestapp.online/oz_backend/mechanics_module/mechanics_products.php';

const MechanicsBarcode = () => {
    const { user, currentShop } = useAuth();
    
    // ─────────────────────────────────────────────────────────
    // 🌍 LANGUAGE & THEME STATE
    // ─────────────────────────────────────────────────────────
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [translations, setTranslations] = useState({});
    const [darkMode, setDarkMode] = useState(() => {
        return localStorage.getItem('ourzest_darkMode') === 'true';
    });
    const [currentThemeColor, setCurrentThemeColor] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#10b981';
    });
    
    // ─────────────────────────────────────────────────────────
    // 📦 STATE MANAGEMENT
    // ─────────────────────────────────────────────────────────
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredProducts, setFilteredProducts] = useState([]);
    
    // 🔧 Barcode Settings
    const [barcodeType, setBarcodeType] = useState('CODE128');
    const [barcodeWidth, setBarcodeWidth] = useState(2);
    const [barcodeHeight, setBarcodeHeight] = useState(50);
    const [fontSize, setFontSize] = useState(10);
    
    // 👁️ TEXT DISPLAY TOGGLES
    const [showProductName, setShowProductName] = useState(true);
    const [showPrice, setShowPrice] = useState(true);
    const [showBusinessName, setShowBusinessName] = useState(false);
    const [showBatchNo, setShowBatchNo] = useState(true);
    
    // 🏢 Business Name Input
    const [businessName, setBusinessName] = useState(() => {
        return localStorage.getItem('business_name') || user?.shop_name || 'Your Business';
    });
    
    // 🖨️ Paper/Print Settings
    const [selectedPaper, setSelectedPaper] = useState('zebra_4x6');
    const [labelSpacing, setLabelSpacing] = useState(2);
    const [orientation, setOrientation] = useState('portrait');
    const [printMode, setPrintMode] = useState('custom');
    const [printCopies, setPrintCopies] = useState(1);
    
    // 👁️ Preview & Print
    const [previewItems, setPreviewItems] = useState([]);
    const [showPreview, setShowPreview] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    
    const printRef = useRef(null);
    const abortControllerRef = useRef(null);
    const toastTimerRef = useRef(null);

    // ─────────────────────────────────────────────────────────
    // 🎨 THEME COLOR AUTO-SYNC (Like AddCustomer)
    // ─────────────────────────────────────────────────────────
    const applyThemeToDocument = useCallback((color) => {
        if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
            color = '#10b981';
        }
        
        document.documentElement.style.setProperty('--ozbarui-primary', color);
        
        let r, g, b;
        if (color.startsWith('#')) {
            r = parseInt(color.slice(1, 3), 16);
            g = parseInt(color.slice(3, 5), 16);
            b = parseInt(color.slice(5, 7), 16);
        } else {
            r = 16; g = 185; b = 129;
        }
        
        let style = document.getElementById('ozbarui-theme-styles');
        if (!style) {
            style = document.createElement('style');
            style.id = 'ozbarui-theme-styles';
            document.head.appendChild(style);
        }
        
        style.textContent = `
            .ozbarui-header-icon {
                background: linear-gradient(135deg, ${color}, rgba(${r}, ${g}, ${b}, 0.8)) !important;
            }
            .ozbarui-btn-print {
                background: linear-gradient(135deg, ${color}, rgba(${r}, ${g}, ${b}, 0.8)) !important;
            }
            .ozbarui-btn-primary {
                background: ${color} !important;
            }
            .ozbarui-btn-small:hover:not(:disabled) {
                background: ${color} !important;
                border-color: ${color} !important;
            }
            .ozbarui-product-item.ozbarui-selected {
                background: rgba(${r}, ${g}, ${b}, 0.05) !important;
                border-color: ${color} !important;
            }
            .ozbarui-mode-option.ozbarui-active {
                border-color: ${color} !important;
                background: rgba(${r}, ${g}, ${b}, 0.05) !important;
            }
            .ozbarui-search-box input:focus {
                border-color: ${color} !important;
                box-shadow: 0 0 0 3px rgba(${r}, ${g}, ${b}, 0.1) !important;
            }
            .ozbarui-panel-header h3 svg {
                color: ${color} !important;
            }
            .ozbarui-price {
                color: ${color} !important;
            }
            .ozbarui-loading-spinner {
                border-top-color: ${color} !important;
            }
            .ozbarui-quantity-control button:hover:not(:disabled) {
                background: ${color} !important;
                border-color: ${color} !important;
            }
            
            .ozbarui-header-bg-animation .gradient-orb.orb-1 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.4) 0%, rgba(${r}, ${g}, ${b}, 0.1) 100%);
            }
            .ozbarui-header-bg-animation .gradient-orb.orb-2 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.35) 0%, rgba(${r}, ${g}, ${b}, 0.08) 100%);
            }
            .ozbarui-header-bg-animation .gradient-orb.orb-3 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.3) 0%, rgba(${r}, ${g}, ${b}, 0.05) 100%);
            }
            .ozbarui-header-bg-animation .gradient-orb.orb-4 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.25) 0%, rgba(${r}, ${g}, ${b}, 0.03) 100%);
            }
            .ozbarui-header-bg-animation .gradient-orb.orb-5 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.2) 0%, rgba(${r}, ${g}, ${b}, 0.02) 100%);
            }
        `;
        
        setCurrentThemeColor(color);
    }, []);

    // Auto-sync theme with dashboard changes
    useEffect(() => {
        const savedTheme = localStorage.getItem('ourzest_theme') || '#10b981';
        applyThemeToDocument(savedTheme);
        
        // Function to force theme update
        const forceThemeUpdate = () => {
            const currentTheme = localStorage.getItem('ourzest_theme') || '#10b981';
            if (currentTheme !== currentThemeColor) {
                applyThemeToDocument(currentTheme);
            }
        };
        
        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_theme') {
                applyThemeToDocument(e.newValue || '#10b981');
            }
        };
        
        const handleThemeChanged = (event) => {
            if (event.detail && event.detail.theme) {
                applyThemeToDocument(event.detail.theme);
            }
        };
        
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('themeChanged', handleThemeChanged);
        
        // Poll for theme changes every second (as fallback)
        const interval = setInterval(forceThemeUpdate, 1000);
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('themeChanged', handleThemeChanged);
            clearInterval(interval);
        };
    }, [applyThemeToDocument, currentThemeColor]);

    // ─────────────────────────────────────────────────────────
    // 🌙 DARK MODE AUTO-SYNC (Like AddCustomer)
    // ─────────────────────────────────────────────────────────
    useEffect(() => {
        const syncDarkMode = () => {
            const isDark = localStorage.getItem('ourzest_darkMode') === 'true';
            setDarkMode(isDark);
            const container = document.querySelector('.ozbarui-container');
            if (container) {
                if (isDark) {
                    container.classList.add('ozbarui-dark');
                } else {
                    container.classList.remove('ozbarui-dark');
                }
            }
            // Also apply to body for global dark mode
            if (isDark) {
                document.body.classList.add('ozbarui-dark');
            } else {
                document.body.classList.remove('ozbarui-dark');
            }
        };
        
        syncDarkMode();
        
        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_darkMode') syncDarkMode();
        };
        
        const handleDarkModeToggle = (event) => {
            const isDark = event.detail?.darkMode ?? localStorage.getItem('ourzest_darkMode') === 'true';
            setDarkMode(isDark);
            const container = document.querySelector('.ozbarui-container');
            if (container) {
                isDark ? container.classList.add('ozbarui-dark') : container.classList.remove('ozbarui-dark');
            }
        };
        
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('darkModeToggled', handleDarkModeToggle);
        
        // Poll for dark mode changes every second (as fallback)
        const interval = setInterval(() => {
            const storedDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            if (storedDarkMode !== darkMode) {
                syncDarkMode();
            }
        }, 1000);
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('darkModeToggled', handleDarkModeToggle);
            clearInterval(interval);
        };
    }, [darkMode]);

    // ─────────────────────────────────────────────────────────
    // 🌍 LANGUAGE AUTO-SYNC (Like AddCustomer)
    // ─────────────────────────────────────────────────────────
    useEffect(() => {
        const loadTranslations = async (lang) => {
            const trans = await barcodeTranslator.getTranslations(lang);
            setTranslations(trans);
        };
        
        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };
        
        const handleCustomLanguageChange = (event) => {
            if (event.detail && event.detail.language) {
                const newLang = event.detail.language;
                if (newLang !== currentLanguage) {
                    setCurrentLanguage(newLang);
                    loadTranslations(newLang);
                }
            }
        };
        
        // Initial load
        loadTranslations(currentLanguage);
        
        window.addEventListener('storage', handleLanguageChange);
        window.addEventListener('languageChanged', handleCustomLanguageChange);
        
        // Poll for language changes every second (as fallback)
        const interval = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 1000);
        
        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            window.removeEventListener('languageChanged', handleCustomLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);
    
    // RTL direction sync
    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
    
    useEffect(() => {
        const container = document.querySelector('.ozbarui-container');
        if (container) {
            if (isRTL) {
                container.classList.add('ozbarui-rtl');
                container.classList.remove('ozbarui-ltr');
            } else {
                container.classList.add('ozbarui-ltr');
                container.classList.remove('ozbarui-rtl');
            }
        }
        document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
        document.body.dir = isRTL ? 'rtl' : 'ltr';
    }, [isRTL]);

    // ─────────────────────────────────────────────────────────
    // 🔔 TOAST NOTIFICATIONS
    // ─────────────────────────────────────────────────────────
    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ show: true, type, message });
        toastTimerRef.current = setTimeout(() => {
            setToast(prev => ({ ...prev, show: false }));
        }, 3000);
    }, []);

    // Cleanup toast timer on unmount
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        };
    }, []);

    // ─────────────────────────────────────────────────────────
    // 📡 API FETCH - PRODUCTS
    // ─────────────────────────────────────────────────────────
    const fetchProducts = useCallback(async () => {
        if (abortControllerRef.current) abortControllerRef.current.abort();
        abortControllerRef.current = new AbortController();

        if (!user?.username) {
            setLoading(false);
            return;
        }

        const shopId = currentShop?.shop_id;
        if (!shopId) {
            setLoading(false);
            setFetchError('Select a shop first');
            showToast('warning', 'Select a shop first');
            return;
        }

        setLoading(true);
        setFetchError(null);

        try {
            const response = await fetch(API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_records',
                    username: user.username.trim(),
                    shop_id: shopId,
                    sort: 'product_name',
                    order: 'ASC'
                }),
                signal: abortControllerRef.current.signal
            });

            const data = await response.json();

            if (data.status === 'success') {
                const productsList = Array.isArray(data.data) ? data.data : [];
                setProducts(productsList);
                setFilteredProducts(productsList);
            } else {
                setFetchError(data.message || 'Failed to fetch products');
                setProducts([]);
                setFilteredProducts([]);
            }
        } catch (error) {
            if (error.name === 'AbortError') return;
            setFetchError(error.message || 'Network error occurred');
        } finally {
            setLoading(false);
        }
    }, [user, currentShop, showToast]);

    useEffect(() => {
        if (user?.username && currentShop) fetchProducts();
        return () => abortControllerRef.current?.abort();
    }, [user, currentShop, fetchProducts]);

    // ─────────────────────────────────────────────────────────
    // 🔍 SEARCH FILTER
    // ─────────────────────────────────────────────────────────
    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredProducts(products);
            return;
        }
        const term = searchTerm.toLowerCase();
        setFilteredProducts(products.filter(p => 
            p.product_name?.toLowerCase().includes(term) ||
            p.batch_no?.toLowerCase().includes(term) ||
            p.barcode?.toLowerCase().includes(term)
        ));
    }, [searchTerm, products]);

    // ─────────────────────────────────────────────────────────
    // ✅ PRODUCT SELECTION HANDLERS
    // ─────────────────────────────────────────────────────────
    const toggleProductSelection = useCallback((product) => {
        setSelectedProducts(prev => {
            const exists = prev.find(p => p.id === product.id);
            if (exists) return prev.filter(p => p.id !== product.id);
            return [...prev, { ...product, quantity: 1, originalQuantity: parseFloat(product.quantity) || 0 }];
        });
    }, []);

    const selectAllProducts = useCallback(() => {
        if (filteredProducts.length === 0) return;
        setSelectedProducts(filteredProducts.map(p => ({ ...p, quantity: 1, originalQuantity: parseFloat(p.quantity) || 0 })));
        showToast('success', `Selected ${filteredProducts.length} products`);
    }, [filteredProducts, showToast]);

    const clearSelection = useCallback(() => {
        setSelectedProducts([]);
        showToast('info', 'Selection cleared');
    }, [showToast]);

    const updateSelectedQuantity = useCallback((productId, newQuantity) => {
        setSelectedProducts(prev => prev.map(p =>
            p.id === productId ? { ...p, quantity: Math.max(1, newQuantity || 1) } : p
        ));
    }, []);

    const removeSelectedProduct = useCallback((productId) => {
        setSelectedProducts(prev => prev.filter(p => p.id !== productId));
    }, []);

    // ─────────────────────────────────────────────────────────
    // 👁️ PREVIEW GENERATION
    // ─────────────────────────────────────────────────────────
    const generatePreview = useCallback(() => {
        if (selectedProducts.length === 0) {
            showToast('error', 'Please select at least one product');
            return;
        }

        const items = [];
        selectedProducts.forEach(product => {
            const baseQty = printMode === 'stock' ? (parseInt(product.originalQuantity) || 0) : (product.quantity || 1);
            if (baseQty <= 0 && printMode === 'stock') return;
            const totalQty = baseQty * printCopies;
            for (let i = 0; i < totalQty; i++) {
                items.push({
                    id: `${product.id}_${i}_${Date.now()}`,
                    product: product,
                    barcode: product.barcode,
                    productName: product.product_name,
                    batchNo: product.batch_no,
                    price: product.sell_price,
                    code: product.product_code
                });
            }
        });

        if (items.length === 0) {
            showToast('error', 'No valid items to print');
            return;
        }

        setPreviewItems(items);
        setShowPreview(true);
        showToast('success', `${items.length} labels ready`);
    }, [selectedProducts, printMode, printCopies, showToast]);

    // ─────────────────────────────────────────────────────────
    // 🖨️ PRINTER & PAPER HELPERS
    // ─────────────────────────────────────────────────────────
    const getPaperSettings = useCallback(() => PAPER_PRESETS[selectedPaper] || PAPER_PRESETS.zebra_4x6, [selectedPaper]);

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        pageStyle: `
            @page { size: ${getPaperSettings().paper}; margin: 0mm; }
            @media print {
                body { -webkit-print-color-adjust: exact; print-color-adjust: exact; margin: 0; padding: 0; }
                .ozbarui-barcode-item { page-break-inside: avoid; break-inside: avoid; border: none !important; }
            }
        `,
        onBeforeGetContent: () => { setIsPrinting(true); return Promise.resolve(); },
        onAfterPrint: () => { setIsPrinting(false); setShowPreview(false); showToast('success', `${previewItems.length} labels printed`); },
        onPrintError: (error) => { setIsPrinting(false); showToast('error', `Print failed: ${error.message}`); }
    });

    // ─────────────────────────────────────────────────────────
    // 🏷️ BARCODE ITEM RENDERER
    // ─────────────────────────────────────────────────────────
    const generateBarcodeItem = useCallback((item, index) => {
        const paper = getPaperSettings();
        return (
            <div key={item.id || index} className="ozbarui-barcode-item" style={{
                width: `${paper.labelWidth}mm`,
                height: `${paper.labelHeight}mm`,
                backgroundColor: '#ffffff',
                border: `0.5px solid ${darkMode ? '#334155' : '#e0e0e0'}`
            }}>
                {showBusinessName && businessName && (
                    <div className="ozbarui-barcode-business">{businessName}</div>
                )}
                <div className="ozbarui-barcode-svg-container">
                    <svg ref={el => {
                        if (el && item.barcode) {
                            JsBarcode(el, item.barcode, {
                                format: barcodeType,
                                width: barcodeWidth,
                                height: barcodeHeight,
                                displayValue: false,
                                margin: 0,
                                background: '#ffffff',
                                lineColor: '#000000'
                            });
                        }
                    }} />
                </div>
                {(showProductName || showPrice || showBatchNo) && (
                    <div className="ozbarui-barcode-info" style={{ fontSize: `${fontSize}pt` }}>
                        {showProductName && item.productName && (
                            <div className="ozbarui-barcode-name">{item.productName}</div>
                        )}
                        {showBatchNo && item.batchNo && (
                            <div className="ozbarui-barcode-batch">{translations.batch || 'Batch'}: {item.batchNo}</div>
                        )}
                        {showPrice && item.price && (
                            <div className="ozbarui-barcode-price">{translations.rs || 'Rs.'} {parseFloat(item.price).toFixed(2)}</div>
                        )}
                    </div>
                )}
            </div>
        );
    }, [barcodeType, barcodeWidth, barcodeHeight, fontSize, showProductName, showPrice, showBatchNo, showBusinessName, businessName, getPaperSettings, darkMode, translations]);

    useEffect(() => {
        if (businessName) localStorage.setItem('business_name', businessName);
    }, [businessName]);

    // ─────────────────────────────────────────────────────────
    // 🎨 RENDER
    // ─────────────────────────────────────────────────────────
    return (
        <div className={`ozbarui-container ${isRTL ? 'ozbarui-rtl' : 'ozbarui-ltr'} ${darkMode ? 'ozbarui-dark' : ''}`}>
            {/* Toast Notification */}
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />

            {/* Sticky Header */}
            <div className="ozbarui-sticky-header">
                <div className="ozbarui-header-bg-animation">
                    <div className="gradient-orb orb-1"></div>
                    <div className="gradient-orb orb-2"></div>
                    <div className="gradient-orb orb-3"></div>
                    <div className="gradient-orb orb-4"></div>
                    <div className="gradient-orb orb-5"></div>
                </div>
                <div className="ozbarui-header-content">
                    <div className="ozbarui-header-left">
                        <div className="ozbarui-header-icon">
                            <FontAwesomeIcon icon={faBarcode} />
                        </div>
                        <div className="ozbarui-header-title">
                            <h1>{translations.barcodeGenerator || 'Barcode Generator'}</h1>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="ozbarui-main-content">
                {/* Left Panel - Products List */}
                <div className="ozbarui-left-panel">
                    <div className="ozbarui-panel-header">
                        <h3><FontAwesomeIcon icon={faBoxes} /> {translations.selectProducts || 'Select Products'}</h3>
                        <div className="ozbarui-panel-actions">
                            <button onClick={selectAllProducts} className="ozbarui-btn-small" disabled={filteredProducts.length === 0 || loading}>
                                {translations.selectAll || 'Select All'}
                            </button>
                            <button onClick={clearSelection} className="ozbarui-btn-small" disabled={selectedProducts.length === 0}>
                                {translations.clear || 'Clear'}
                            </button>
                        </div>
                    </div>

                    <div className="ozbarui-search-box">
                        <FontAwesomeIcon icon={faSearch} className="ozbarui-search-icon" />
                        <input type="text" placeholder={translations.searchProducts || 'Search products...'} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    </div>

                    <div className="ozbarui-products-list">
                        {loading ? (
                            <div className="ozbarui-loading-container">
                                <div className="ozbarui-loading-spinner"></div>
                                <p>{translations.loadingProducts || 'Loading products...'}</p>
                            </div>
                        ) : fetchError ? (
                            <div className="ozbarui-error-container">
                                <FontAwesomeIcon icon={faExclamationTriangle} size="2x" />
                                <p>{fetchError}</p>
                                <button onClick={fetchProducts} className="ozbarui-btn-retry">
                                    <FontAwesomeIcon icon={faRefresh} /> {translations.retry || 'Retry'}
                                </button>
                            </div>
                        ) : filteredProducts.length === 0 ? (
                            <div className="ozbarui-no-products">
                                <FontAwesomeIcon icon={faBoxes} size="2x" />
                                <p>{translations.noProductsFound || 'No products found'}</p>
                            </div>
                        ) : (
                            filteredProducts.map(product => {
                                const isSelected = selectedProducts.some(p => p.id === product.id);
                                return (
                                    <div 
                                        key={product.id} 
                                        className={`ozbarui-product-item ${isSelected ? 'ozbarui-selected' : ''}`} 
                                        onClick={() => toggleProductSelection(product)}
                                    >
                                        <div className="ozbarui-product-checkbox">
                                            <input 
                                                type="checkbox" 
                                                checked={isSelected} 
                                                onChange={() => toggleProductSelection(product)} 
                                                onClick={(e) => e.stopPropagation()} 
                                            />
                                        </div>
                                        <div className="ozbarui-product-info">
                                            <div className="ozbarui-product-name-col">
                                                <div className="ozbarui-product-name" title={product.product_name}>
                                                    {product.product_name}
                                                </div>
                                            </div>
                                            <div className="ozbarui-product-batch-col">
                                                <div className="ozbarui-product-batch" title={product.batch_no || 'N/A'}>
                                                    {product.batch_no || '-'}
                                                </div>
                                            </div>
                                            <div className="ozbarui-product-code-col">
                                                <div className="ozbarui-product-code" title={product.product_code || 'N/A'}>
                                                    {product.product_code || '-'}
                                                </div>
                                            </div>
                                            <div className="ozbarui-product-stock-col">
                                                <div className="ozbarui-stock">
                                                    {parseFloat(product.quantity).toFixed(2)}
                                                </div>
                                            </div>
                                            <div className="ozbarui-product-price-col">
                                                <div className="ozbarui-price">
                                                    {translations.rs || 'Rs.'} {parseFloat(product.sell_price).toFixed(2)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* Right Panel - Settings */}
                <div className="ozbarui-right-panel">
                    <div className="ozbarui-selected-section">
                        <h3><FontAwesomeIcon icon={faTags} /> {translations.selected || 'Selected'} ({selectedProducts.length})</h3>
                        {selectedProducts.length === 0 ? (
                            <div className="ozbarui-no-selection">
                                <p>{translations.noProductsSelected || 'No products selected'}</p>
                            </div>
                        ) : (
                            <div className="ozbarui-selected-items-list">
                                {selectedProducts.map(product => (
                                    <div key={product.id} className="ozbarui-selected-item">
                                        <div className="ozbarui-item-info">
                                            <div className="ozbarui-item-name-col">
                                                <div className="ozbarui-item-name" title={product.product_name}>
                                                    {product.product_name}
                                                </div>
                                            </div>
                                            <div className="ozbarui-item-batch-col">
                                                <div className="ozbarui-item-batch" title={product.batch_no || 'N/A'}>
                                                    {product.batch_no || '-'}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="ozbarui-item-controls">
                                            <div className="ozbarui-quantity-control">
                                                <button onClick={() => updateSelectedQuantity(product.id, product.quantity - 1)} disabled={product.quantity <= 1 || printMode === 'stock'}>
                                                    <FontAwesomeIcon icon={faMinus} size="xs" />
                                                </button>
                                                <input type="number" min="1" value={product.quantity} onChange={(e) => updateSelectedQuantity(product.id, parseInt(e.target.value) || 1)} disabled={printMode === 'stock'} />
                                                <button onClick={() => updateSelectedQuantity(product.id, product.quantity + 1)} disabled={printMode === 'stock'}>
                                                    <FontAwesomeIcon icon={faPlus} size="xs" />
                                                </button>
                                            </div>
                                            <button className="ozbarui-remove-btn" onClick={() => removeSelectedProduct(product.id)}>
                                                <FontAwesomeIcon icon={faTimes} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="ozbarui-settings-section">
                        <h3><FontAwesomeIcon icon={faCog} /> {translations.barcodeStyle || 'Barcode Style'}</h3>
                        <div className="ozbarui-settings-grid">
                            <div className="ozbarui-setting-item">
                                <label>{translations.type || 'Type'}</label>
                                <select value={barcodeType} onChange={(e) => setBarcodeType(e.target.value)}>
                                    <option value="CODE128">CODE128</option>
                                    <option value="CODE39">CODE39</option>
                                    <option value="EAN13">EAN13</option>
                                </select>
                            </div>
                            <div className="ozbarui-setting-item">
                                <label>{translations.width || 'Width'}: {barcodeWidth}</label>
                                <input type="range" min="1" max="4" step="0.1" value={barcodeWidth} onChange={(e) => setBarcodeWidth(parseFloat(e.target.value))} />
                            </div>
                            <div className="ozbarui-setting-item">
                                <label>{translations.height || 'Height'}: {barcodeHeight}px</label>
                                <input type="range" min="30" max="100" value={barcodeHeight} onChange={(e) => setBarcodeHeight(parseInt(e.target.value))} />
                            </div>
                            <div className="ozbarui-setting-item">
                                <label>{translations.fontSize || 'Font Size'}: {fontSize}pt</label>
                                <input type="range" min="6" max="16" value={fontSize} onChange={(e) => setFontSize(parseInt(e.target.value))} />
                            </div>
                        </div>
                    </div>

                    <div className="ozbarui-settings-section">
                        <h3><FontAwesomeIcon icon={faEye} /> {translations.labelContent || 'Label Content'}</h3>
                        <div className="ozbarui-toggle-grid">
                            <label className="ozbarui-toggle-item">
                                <input type="checkbox" checked={showProductName} onChange={(e) => setShowProductName(e.target.checked)} />
                                {translations.productName || 'Product Name'}
                            </label>
                            <label className="ozbarui-toggle-item">
                                <input type="checkbox" checked={showPrice} onChange={(e) => setShowPrice(e.target.checked)} />
                                {translations.price || 'Price'}
                            </label>
                            <label className="ozbarui-toggle-item">
                                <input type="checkbox" checked={showBatchNo} onChange={(e) => setShowBatchNo(e.target.checked)} />
                                {translations.batchNo || 'Batch No'}
                            </label>
                            <label className="ozbarui-toggle-item">
                                <input type="checkbox" checked={showBusinessName} onChange={(e) => setShowBusinessName(e.target.checked)} />
                                {translations.businessName || 'Business Name'}
                            </label>
                        </div>
                        {showBusinessName && (
                            <input type="text" className="ozbarui-business-input" value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder={translations.businessName || 'Business Name'} />
                        )}
                    </div>

                    <div className="ozbarui-settings-section">
                        <h3><FontAwesomeIcon icon={faPrint} /> {translations.paperPrinter || 'Paper & Printer'}</h3>
                        <div className="ozbarui-settings-grid">
                            <div className="ozbarui-setting-item ozbarui-full-width">
                                <label>{translations.labelSheet || 'Label Sheet'}</label>
                                <select value={selectedPaper} onChange={(e) => setSelectedPaper(e.target.value)}>
                                    {Object.entries(PAPER_PRESETS).map(([key, p]) => (
                                        <option key={key} value={key}>{p.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="ozbarui-setting-item">
                                <label>{translations.spacing || 'Spacing'}: {labelSpacing}mm</label>
                                <input type="range" min="0" max="8" step="0.5" value={labelSpacing} onChange={(e) => setLabelSpacing(parseFloat(e.target.value))} />
                            </div>
                            <div className="ozbarui-setting-item">
                                <label>{translations.orientation || 'Orientation'}</label>
                                <select value={orientation} onChange={(e) => setOrientation(e.target.value)}>
                                    <option value="portrait">{translations.portrait || 'Portrait'}</option>
                                    <option value="landscape">{translations.landscape || 'Landscape'}</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="ozbarui-settings-section">
                        <h3><FontAwesomeIcon icon={faSlidersH} /> {translations.quantity || 'Quantity'}</h3>
                        <div className="ozbarui-print-mode-selector">
                            <label className={`ozbarui-mode-option ${printMode === 'custom' ? 'ozbarui-active' : ''}`}>
                                <input type="radio" name="mode" value="custom" checked={printMode === 'custom'} onChange={(e) => setPrintMode(e.target.value)} />
                                <div className="ozbarui-mode-content">
                                    <span className="ozbarui-mode-title">{translations.custom || 'Custom'}</span>
                                    <small>{translations.setPerProduct || 'Set per product'}</small>
                                </div>
                            </label>
                            <label className={`ozbarui-mode-option ${printMode === 'stock' ? 'ozbarui-active' : ''}`}>
                                <input type="radio" name="mode" value="stock" checked={printMode === 'stock'} onChange={(e) => setPrintMode(e.target.value)} />
                                <div className="ozbarui-mode-content">
                                    <span className="ozbarui-mode-title">{translations.stockQty || 'Stock Qty'}</span>
                                    <small>{translations.onePerUnit || '1 per unit'}</small>
                                </div>
                            </label>
                        </div>
                        <div className="ozbarui-print-copies">
                            <label>{translations.printCopies || 'Print Copies'}: {printCopies}</label>
                            <input type="number" min="1" max="100" value={printCopies} onChange={(e) => setPrintCopies(parseInt(e.target.value) || 1)} />
                        </div>
                    </div>

                    <div className="ozbarui-action-buttons">
                        <button className="ozbarui-btn-preview" onClick={generatePreview} disabled={selectedProducts.length === 0}>
                            <FontAwesomeIcon icon={faEye} /> {translations.preview || 'Preview'}
                        </button>
                        <button className="ozbarui-btn-print" onClick={handlePrint} disabled={previewItems.length === 0 || isPrinting}>
                            {isPrinting ? <><FontAwesomeIcon icon={faSpinner} spin /> {translations.printing || 'Printing...'}</> : <><FontAwesomeIcon icon={faPrint} /> {translations.print || 'Print'} ({previewItems.length})</>}
                        </button>
                    </div>
                </div>
            </div>

            {/* Preview Modal */}
            {showPreview && (
                <div className="ozbarui-preview-modal" onClick={() => setShowPreview(false)}>
                    <div className="ozbarui-preview-content" onClick={(e) => e.stopPropagation()}>
                        <div className="ozbarui-preview-header">
                            <h3><FontAwesomeIcon icon={faEye} /> {translations.preview || 'Print Preview'}</h3>
                            <button className="ozbarui-close-btn" onClick={() => setShowPreview(false)}><FontAwesomeIcon icon={faTimes} /></button>
                        </div>
                        <div className="ozbarui-preview-body">
                            <div className="ozbarui-preview-info">
                                <span>📄 {previewItems.length} labels</span>
                                <span>📐 {getPaperSettings().name}</span>
                            </div>
                            <div ref={printRef} className="ozbarui-print-grid" style={{
                                display: 'grid',
                                gridTemplateColumns: `repeat(${getPaperSettings().labelsPerRow}, 1fr)`,
                                gap: `${labelSpacing}mm`,
                                padding: `${getPaperSettings().marginTop}mm ${getPaperSettings().marginLeft}mm`,
                                background: '#fff'
                            }}>
                                {previewItems.map((item, idx) => generateBarcodeItem(item, idx))}
                            </div>
                        </div>
                        <div className="ozbarui-preview-footer">
                            <button className="ozbarui-btn-secondary" onClick={() => setShowPreview(false)}>{translations.cancel || 'Cancel'}</button>
                            <button className="ozbarui-btn-primary" onClick={handlePrint}><FontAwesomeIcon icon={faPrint} /> {translations.printNow || 'Print Now'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MechanicsBarcode;